import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveApiMediaUrl } from '../../../api/client.js';
import { firstDisplayImageRaw } from '../../../utils/productImage.js';
import { CatalogLuxeProductModal } from './CatalogLuxeProductModal.jsx';
import { ProductLowStockHint } from '../ProductLowStockHint.jsx';
import { ProductNewHint } from '../ProductNewHint.jsx';
import './catalog-luxe.css';

function productDisplayImageUrl(p, productImageCtx) {
  const raw = firstDisplayImageRaw(p, productImageCtx);
  return raw ? resolveApiMediaUrl(raw) : '';
}

/**
 * Loja pública em modo «Catálogo luxo» — layout editorial; sacola/checkout ficam no StorePage.
 * @param {Record<string, unknown>} props
 */
export function StoreCatalogLuxeView(props) {
  const {
    config,
    tenant,
    slug,
    products = [],
    sections = [],
    filters,
    setFilters,
    cart = {},
    addQty,
    formatBrl,
    pricingHidden = false,
    catalogOnly = false,
    blockSaleZeroStock = false,
    cartCount = 0,
    cartTotal = 0,
    onOpenDrawer,
    onRequireLogin,
    onOpenMediaPreview,
    productImageCtx,
    productStockCap,
    deepLinkProductId = 0,
  } = props;

  const [modalProduct, setModalProduct] = useState(null);
  const [searchLocal, setSearchLocal] = useState(String(filters?.search || ''));

  const tenantName = String(tenant?.nome || 'Loja');
  const logoUrl = tenant?.logo_url ? resolveApiMediaUrl(String(tenant.logo_url)) : '';
  const waDigits = String(tenant?.contact_whatsapp || '').replace(/\D+/g, '');
  const waHref = waDigits ? `https://wa.me/${waDigits}` : null;

  const activeSecao = String(filters?.secao || '').trim();
  const filteredProducts = useMemo(() => {
    let list = Array.isArray(products) ? [...products] : [];
    const q = searchLocal.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const nome = String(p?.nome || '').toLowerCase();
        const cod = String(p?.codigo_erp || p?.id || '').toLowerCase();
        return nome.includes(q) || cod.includes(q);
      });
    }
    return list;
  }, [products, searchLocal]);

  const categoryPills = useMemo(() => {
    const names = Array.isArray(sections) ? sections.filter((s) => String(s).trim() !== '') : [];
    return ['', ...names];
  }, [sections]);

  useEffect(() => {
    if (!deepLinkProductId || !products.length) return;
    const found = products.find((p) => Number(p.id) === Number(deepLinkProductId));
    if (found) setModalProduct(found);
  }, [deepLinkProductId, products]);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => ({ ...f, search: searchLocal }));
    }, 280);
    return () => clearTimeout(t);
  }, [searchLocal, setFilters]);

  function openProduct(p) {
    setModalProduct(p);
  }

  function stockCap(p) {
    if (typeof productStockCap === 'function') return productStockCap(p);
    return null;
  }

  const modalId = modalProduct ? Number(modalProduct.id) : 0;
  const modalQty = modalId ? Number(cart[modalId] || 0) : 0;
  const modalCap = modalProduct ? stockCap(modalProduct) : null;
  const modalSoldOut = modalCap !== null && modalCap <= 0;
  const modalAtCap = modalCap != null && modalQty >= modalCap;

  return (
    <div className="catalog-luxe">
      <header className="cl-nav">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
          <Link to={`/${encodeURIComponent(slug)}`} className="flex min-w-0 items-baseline gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="h-9 w-auto max-w-[120px] object-contain" />
            ) : (
              <span className="cl-display cl-gold-text text-2xl">{tenantName}</span>
            )}
          </Link>
          <nav className="hidden items-center gap-6 text-sm text-[var(--cl-muted)] md:flex">
            <a href="#catalogo-luxe" className="hover:text-[var(--cl-fg)]">
              Catálogo
            </a>
            {config.howItWorks?.enabled ? (
              <a href="#como-luxe" className="hover:text-[var(--cl-fg)]">
                Como funciona
              </a>
            ) : null}
          </nav>
          <div className="flex items-center gap-2">
            {!catalogOnly && cartCount > 0 ? (
              <button type="button" className="cl-btn-primary py-2 text-xs" onClick={onOpenDrawer}>
                Sacola · {cartCount}
              </button>
            ) : null}
            {waHref ? (
              <a href={waHref} target="_blank" rel="noreferrer" className="cl-btn-outline hidden py-2 text-xs sm:inline-flex">
                WhatsApp
              </a>
            ) : null}
          </div>
        </div>
      </header>

      <section className="cl-grain relative overflow-hidden pb-20 pt-28 md:pt-36">
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-[1.1fr_1fr]">
          <div>
            {config.hero?.badge ? (
              <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-[oklch(0.78_0.13_80/40)] px-3 py-1 text-xs uppercase tracking-[0.25em] text-[var(--cl-gold)]">
                {config.hero.badge}
              </div>
            ) : null}
            <h1 className="cl-display text-5xl leading-[0.95] md:text-7xl lg:text-8xl">
              {config.hero?.titleBefore || 'Presentes que'}{' '}
              <span className="cl-script cl-gold-text block text-6xl leading-none md:text-8xl lg:text-9xl">
                {config.hero?.titleAccent || 'falam de amor'}
              </span>
            </h1>
            {config.hero?.subtitle ? (
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-[var(--cl-muted)]">{config.hero.subtitle}</p>
            ) : null}
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#catalogo-luxe" className="cl-btn-primary">
                {config.hero?.ctaCatalog || 'Ver o catálogo'}
              </a>
              {waHref ? (
                <a href={waHref} target="_blank" rel="noreferrer" className="cl-btn-outline">
                  Falar com a loja
                </a>
              ) : null}
            </div>
          </div>
          {config.hero?.image_url ? (
            <div className="relative hidden aspect-[4/5] overflow-hidden rounded-2xl border border-[oklch(0.78_0.13_80/25)] lg:block">
              <img
                src={resolveApiMediaUrl(config.hero.image_url)}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>
          ) : null}
        </div>
      </section>

      {config.marquee?.items?.length ? (
        <div className="overflow-hidden border-y border-[var(--cl-border)] bg-[oklch(0.2_0.025_25/40)] py-4">
          <div className="cl-marquee-track">
            {[...config.marquee.items, ...config.marquee.items, ...config.marquee.items].map((t, i) => (
              <span key={`${t}-${i}`} className="text-sm uppercase tracking-[0.3em] text-[var(--cl-muted)]">
                {t} ·
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {(config.story?.kicker || config.story?.title) && (
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-4xl px-6 text-center">
            {config.story.kicker ? (
              <div className="mb-6 text-xs uppercase tracking-[0.3em] text-[var(--cl-gold)]">{config.story.kicker}</div>
            ) : null}
            <h2 className="cl-display text-4xl leading-tight md:text-6xl">
              {config.story.title}{' '}
              {config.story.titleAccent ? (
                <em className="cl-script cl-gold-text text-5xl md:text-7xl">{config.story.titleAccent}</em>
              ) : null}
            </h2>
            <div className="cl-divider mx-auto mt-12 w-24" />
          </div>
        </section>
      )}

      <section id="catalogo-luxe" className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              {config.catalog?.kicker ? (
                <div className="mb-4 text-xs uppercase tracking-[0.3em] text-[var(--cl-gold)]">{config.catalog.kicker}</div>
              ) : null}
              <h2 className="cl-display max-w-2xl text-4xl leading-tight md:text-6xl">
                {config.catalog?.titleBefore || 'Surpresas que'}{' '}
                <em className="cl-script cl-gold-text text-5xl md:text-7xl">
                  {config.catalog?.titleAccent || 'emocionam'}
                </em>
              </h2>
            </div>
            <input
              type="search"
              value={searchLocal}
              onChange={(e) => setSearchLocal(e.target.value)}
              placeholder="Buscar produto…"
              className="max-w-sm rounded-full border border-[var(--cl-border)] bg-[oklch(0.2_0.025_25)] px-4 py-2.5 text-sm outline-none focus:border-[var(--cl-gold)]"
              aria-label="Buscar no catálogo"
            />
          </div>

          {categoryPills.length > 1 ? (
            <div className="mb-10 flex flex-wrap gap-2">
              {categoryPills.map((c) => (
                <button
                  key={c || 'todas'}
                  type="button"
                  className={`cl-pill ${activeSecao === c ? 'is-active' : ''}`}
                  onClick={() => setFilters((f) => ({ ...f, secao: c }))}
                >
                  {c === '' ? 'Todos' : c}
                </button>
              ))}
            </div>
          ) : null}

          {!filteredProducts.length ? (
            <p className="py-16 text-center text-[var(--cl-muted)]">Nenhum produto encontrado.</p>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredProducts.map((p) => {
                const pid = Number(p.id);
                const q = Number(cart[pid] || 0);
                const cap = stockCap(p);
                const soldOut = cap !== null && cap <= 0;
                const atCap = cap != null && q >= cap;
                const img = productDisplayImageUrl(p, productImageCtx);
                return (
                  <article
                    key={pid}
                    className="cl-card group cursor-pointer"
                    onClick={() => openProduct(p)}
                    onKeyDown={(e) => e.key === 'Enter' && openProduct(p)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="relative aspect-[4/5] overflow-hidden bg-[oklch(0.22_0.1_20)]">
                      {img ? (
                        <img
                          src={img}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-xs text-[var(--cl-muted)]">
                          Sem foto
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[var(--cl-bg)] via-transparent to-transparent opacity-70" />
                      <ProductNewHint product={p} />
                      {!soldOut ? <ProductLowStockHint product={p} /> : null}
                    </div>
                    <div className="p-6" onClick={(e) => e.stopPropagation()}>
                      <h3 className="cl-display text-2xl">{String(p.nome || 'Produto')}</h3>
                      {!pricingHidden ? (
                        <p className="cl-display cl-gold-text mt-2 text-2xl">{formatBrl(Number(p.preco || 0))}</p>
                      ) : null}
                      {!catalogOnly ? (
                        <div className="mt-4 flex items-center gap-2">
                          {pricingHidden ? (
                            <button type="button" className="cl-btn-primary w-full py-2 text-xs" onClick={onRequireLogin}>
                              Entrar
                            </button>
                          ) : soldOut ? (
                            <span className="text-xs font-semibold text-amber-400">Sem stock</span>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--cl-border)]"
                                onClick={() => addQty(pid, -1)}
                              >
                                −
                              </button>
                              <span className="min-w-[1.5rem] text-center font-bold tabular-nums">{q}</span>
                              <button
                                type="button"
                                className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--cl-gold)] font-bold text-[oklch(0.18_0.02_25)]"
                                onClick={() => addQty(pid, 1)}
                              >
                                +
                              </button>
                              <button
                                type="button"
                                className="cl-btn-primary ml-auto py-2 text-xs"
                                onClick={() => addQty(pid, 1)}
                              >
                                Comprar
                              </button>
                            </>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {config.howItWorks?.enabled ? (
        <section id="como-luxe" className="border-y border-[var(--cl-border)] bg-[oklch(0.2_0.025_25/35)] py-20 md:py-28">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <div className="mb-4 text-xs uppercase tracking-[0.3em] text-[var(--cl-gold)]">{config.howItWorks.kicker}</div>
              <h2 className="cl-display text-4xl md:text-5xl">
                {config.howItWorks.titleBefore}{' '}
                <em className="cl-script cl-gold-text text-5xl md:text-6xl">{config.howItWorks.titleAccent}</em>
              </h2>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {config.howItWorks.steps.map((s, i) => (
                <div
                  key={`${s.title}-${i}`}
                  className="rounded-2xl border border-[var(--cl-border)] bg-[var(--cl-bg)] p-7"
                >
                  <div className="cl-display mb-4 text-4xl text-[oklch(0.78_0.13_80/20)]">0{i + 1}</div>
                  <h3 className="cl-display mb-2 text-xl">{s.title}</h3>
                  <p className="text-sm text-[var(--cl-muted)]">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {config.customBlock?.enabled ? (
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <div className="mb-4 text-xs uppercase tracking-[0.3em] text-[var(--cl-gold)]">{config.customBlock.kicker}</div>
            <h2 className="cl-display text-4xl md:text-5xl">{config.customBlock.title}</h2>
            <p className="mt-6 text-[var(--cl-muted)]">{config.customBlock.subtitle}</p>
            {waHref ? (
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                className="cl-btn-primary mt-10 inline-flex"
              >
                {config.customBlock.ctaLabel}
              </a>
            ) : null}
          </div>
        </section>
      ) : null}

      {!catalogOnly && cartCount > 0 ? (
        <div className="cl-cart-bar">
          <button type="button" className="cl-btn-primary mx-auto flex w-full max-w-lg" onClick={onOpenDrawer}>
            Ver sacola · {cartCount} · {pricingHidden ? '—' : formatBrl(cartTotal)}
          </button>
        </div>
      ) : null}

      <CatalogLuxeProductModal
        product={modalProduct}
        open={Boolean(modalProduct)}
        onClose={() => setModalProduct(null)}
        qty={modalQty}
        formatBrl={formatBrl}
        pricingHidden={pricingHidden}
        catalogOnly={catalogOnly}
        soldOut={modalSoldOut}
        atStockCap={modalAtCap}
        onAddQty={(d) => modalId && addQty(modalId, d)}
        onRequireLogin={onRequireLogin}
        productImageCtx={productImageCtx}
        hasGallery={Boolean(modalProduct && firstDisplayImageRaw(modalProduct, productImageCtx))}
        onOpenGallery={
          modalProduct && onOpenMediaPreview
            ? () => {
                const urls = productGalleryUrls(modalProduct, productImageCtx).map((u) => resolveApiMediaUrl(u));
                onOpenMediaPreview({
                  title: String(modalProduct.nome || 'Produto'),
                  imageUrls: urls,
                  youtubeUrl: modalProduct.youtube_video_url || modalProduct.youtube_url || '',
                  startIndex: 0,
                  startTab: urls.length > 0 ? 'imagens' : 'video',
                });
              }
            : undefined
        }
      />
    </div>
  );
}
